import type { MediaDto } from "@/lib/api";

const API_PREFIX = "/api";

/** Host cotto a build-time: usato SOLO come fallback se l'env runtime manca. */
const BAKED_API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";

/**
 * Host pubblico del backend media, risolto a **RUNTIME**.
 *
 * <p>Permette a UNA sola immagine frontend di servire i media dal backend dello
 * stack corrente cambiando solo un env, senza ricompilare:</p>
 * <ul>
 *   <li><b>client</b>: {@code window.__PUBLIC_MEDIA_BASE__} (iniettato dal root
 *       layout leggendo l'env runtime {@code PUBLIC_MEDIA_BASE_URL});</li>
 *   <li><b>server</b>: {@code process.env.PUBLIC_MEDIA_BASE_URL} (runtime);</li>
 *   <li><b>fallback</b>: {@code NEXT_PUBLIC_API_URL} (cotto). Se l'env runtime
 *       NON è impostato, comportamento IDENTICO a prima (Quarkus invariato).</li>
 * </ul>
 *
 * <p>Server e client leggono lo STESSO valore → nessun hydration mismatch.</p>
 */
function publicApiBase(): string {
  if (typeof window !== "undefined") {
    const w = window as unknown as { __PUBLIC_MEDIA_BASE__?: string };
    const v = w.__PUBLIC_MEDIA_BASE__;
    if (v && v.trim()) return v.trim();
  } else {
    const v = process.env.PUBLIC_MEDIA_BASE_URL;
    if (v && v.trim()) return v.trim();
  }
  return BAKED_API_BASE;
}

function joinPath(basePath: string, path: string): string {
  const base = basePath.replace(/\/$/, "");
  const next = path.startsWith("/") ? path : `/${path}`;
  return `${base}${next}`.replace(/\/+/g, "/");
}

function toBackendUrl(path: string): string {
  let base: URL;
  try {
    base = new URL(publicApiBase());
  } catch {
    base = new URL(BAKED_API_BASE);
  }
  const origin = base.origin;
  const basePath = base.pathname.replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const hasApiPrefix = normalized === API_PREFIX || normalized.startsWith(`${API_PREFIX}/`);
  const baseAlreadyUnderApi = basePath === API_PREFIX || basePath.endsWith(`${API_PREFIX}`);
  const fullPath =
    hasApiPrefix || baseAlreadyUnderApi
      ? joinPath(basePath, normalized)
      : joinPath(basePath, `${API_PREFIX}${normalized}`);
  return new URL(fullPath, origin).toString();
}

/**
 * Sentinella placeholder usata dal backend Quarkus per i messaggi WhatsApp
 * "media-only" (PDF/immagine senza URL web): {@code media://<id>}
 * (cfr. WhatsAppMediaDownloadService.mediaPlaceholderUrl). Serve solo a
 * soddisfare il vincolo UNIQUE su url_hash — NON è una vera URL esterna e
 * NON va mostrata come link (darebbe 404). Difensivo: gestiamo anche la
 * forma {@code file://media/<id>}.
 */
const MEDIA_PLACEHOLDER_RE = /^(?:media:\/\/|file:\/\/media\/)(\d+)$/i;

/** True se la stringa è il placeholder interno di un import media-only. */
export function isMediaPlaceholderUrl(raw: string | null | undefined): boolean {
  return !!raw && MEDIA_PLACEHOLDER_RE.test(raw.trim());
}

/**
 * Normalizza una "urlEsterna" per la VISUALIZZAZIONE come link:
 *  - placeholder media-only ({@code media://<id>}) → URL pubblica del media
 *    ({@code /api/media/<id>}, assoluta) così il link apre davvero il file;
 *  - URL reale → invariata;
 *  - vuota/null → null.
 */
export function resolveExternalUrl(raw: string | null | undefined): string | null {
  const v = raw?.trim();
  if (!v) return null;
  const m = v.match(MEDIA_PLACEHOLDER_RE);
  if (m) return toBackendUrl(`/media/${m[1]}`);
  return v;
}

/**
 * Risolve un MediaDto nel suo URL visualizzabile.
 * Restituisce null se media è null/undefined.
 */
export function resolveMediaUrl(media: MediaDto | null | undefined): string | null {
  if (!media) return null;

  const raw = media.url?.trim();
  if (raw) {
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/")) return toBackendUrl(raw);
    return toBackendUrl(`/${raw}`);
  }

  return toBackendUrl(`/media/${media.id}`);
}

/**
 * Controlla se un MediaDto è un'immagine (non PDF, non video, ecc.)
 * basandosi su mimeType o filename.
 */
export function isImageMedia(media: MediaDto | null | undefined): boolean {
  if (!media) return false;

  // Controlla il mimeType se disponibile
  if (media.mimeType) {
    return media.mimeType.startsWith("image/");
  }

  // Fallback: controlla l'estensione del filename
  if (media.filename) {
    const ext = media.filename.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(ext ?? "");
  }

  return false;
}

/**
 * Risolve un MediaDto SOLO se è un'immagine.
 * Per i PDF e altri formati restituisce null.
 * Utile per le thumbnail/locandine nelle card.
 */
export function resolveImageUrl(media: MediaDto | null | undefined): string | null {
  if (!isImageMedia(media)) return null;
  return resolveMediaUrl(media);
}
